import { ProjectSectionHeader } from '@/components/projects/ProjectSectionHeader'
import { ChainRunsTab } from '@/components/projects/chains/runs'
import { WorkflowRunsTab } from '@/components/projects/workflows/runs'
import { TabBar, tabId, tabPanelId, type TabBarItem } from '@/components/TabBar'
import { useAuth } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { useCallback } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'

type RunsTab = 'individual' | 'chains'

const RUNS_TABS = 'runs-tabs'

export function WorkflowRunsListPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const [searchParams, setSearchParams] = useSearchParams()

  const canViewChains = hasPermission(Permissions.Project.Chain.View, projectId)
  const activeTab: RunsTab = searchParams.get('tab') === 'chains' && canViewChains ? 'chains' : 'individual'

  const handleTabChange = useCallback((tab: RunsTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (tab === 'chains') next.set('tab', tab)
      else next.delete('tab')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const tabs: TabBarItem<RunsTab>[] = [
    { key: 'individual', label: 'Individual workflows' },
    ...(canViewChains ? [{ key: 'chains' as const, label: 'Chains' }] : []),
  ]

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.Workflow.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  return (
    <div className="space-y-6">
      <ProjectSectionHeader sectionName="Runs" />

      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} className="w-fit" idPrefix={RUNS_TABS} />

      <div id={tabPanelId(RUNS_TABS)} role="tabpanel" aria-labelledby={tabId(RUNS_TABS, activeTab)}>
        {activeTab === 'chains' ? <ChainRunsTab /> : <WorkflowRunsTab key={projectId} />}
      </div>
    </div>
  )
}
